import React, { useState, useEffect } from 'react'
import { 
  Settings, 
  User, 
  Palette, 
  Globe, 
  Bell, 
  Shield, 
  Download, 
  Upload, 
  HelpCircle, 
  X,
  Save,
  RotateCcw,
  HardDrive,
  Database,
  Activity,
  Cloud,
  RefreshCw,
  Trash2,
  Cpu,
  Wifi,
  BarChart3,
  CheckCircle,
  AlertTriangle,
  Clock
} from 'lucide-react'
import { useAuth } from '../stores/authStore'
import settingsService from '../services/settingsService'
import imageStorageService from '../services/imageStorageService'
import StorageMonitor from './StorageMonitor'
import simpleDataPersistenceService from '../services/simpleDataPersistenceServiceV2'
import localCloudService from '../services/localCloudService'
import { useStoryStore } from '../stores/storyStore'
import performanceMonitor from '../services/performanceMonitor'
import memoryManager from '../services/memoryManager'
import renderOptimizer from '../services/renderOptimizer'
import networkOptimizer from '../services/networkOptimizer'
import toast from 'react-hot-toast'

export const UserSettings = ({ isOpen, onClose }) => {
  const { user, updateUser } = useAuth()
  const [activeTab, setActiveTab] = useState('profile')
  const [settings, setSettings] = useState(() => settingsService.getSettings())
  const [isSaving, setIsSaving] = useState(false)
  const [showStorageMonitor, setShowStorageMonitor] = useState(false)
  const [storageUsage, setStorageUsage] = useState(null)
  const [dataStats, setDataStats] = useState(null)
  const [syncStatus, setSyncStatus] = useState(null)
  const [performanceMetrics, setPerformanceMetrics] = useState({})
  const [isMonitoring, setIsMonitoring] = useState(false)
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [storageInfo, setStorageInfo] = useState({
    used: 0,
    maxSize: 0,
    usagePercent: 0,
    itemCount: 0,
    items: [],
    localStats: null,
    cloudStats: null
  })
  const [isCleaning, setIsCleaning] = useState(false)
  const { stories, characters } = useStoryStore()

  // 用户偏好设置
  const [preferences, setPreferences] = useState({
    language: '中文',
    theme: 'dark',
    autoSave: true,
    notifications: true,
    soundEffects: true,
    animations: true
  })

  useEffect(() => {
    if (isOpen) {
      // 加载用户设置
      const userSettings = settingsService.getSettings()
      setSettings(userSettings)
      
      // 加载用户偏好
      const savedPreferences = localStorage.getItem('userPreferences')
      if (savedPreferences) {
        setPreferences(JSON.parse(savedPreferences))
      }
      
      // 加载存储使用情况
      try {
        const usage = imageStorageService.getStorageUsage()
        setStorageUsage(usage)
      } catch (error) {
        console.error('获取存储使用情况失败:', error)
      }
      
      // 加载数据管理统计
      loadDataStats()
      
      // 加载存储空间信息
      checkStorageSpace()
      
      // 启动性能监控
      if (isOpen) {
        startPerformanceMonitoring()
      }
    }
    
    return () => {
      if (isMonitoring) {
        setIsMonitoring(false)
      }
    }
  }, [isOpen])
  
  useEffect(() => {
    if (isMonitoring && autoRefresh && isOpen) {
      const interval = setInterval(updatePerformanceMetrics, 5000)
      return () => clearInterval(interval)
    }
  }, [isMonitoring, autoRefresh, isOpen])
  
  const loadDataStats = async () => {
    try {
      const statsResult = await simpleDataPersistenceService.getStorageStats()
      if (statsResult.success) {
        setDataStats(statsResult.stats)
      }
      setSyncStatus({
        inProgress: false,
        lastSync: statsResult.stats?.lastSync || null,
        conflictCount: 0,
        queueSize: 0,
        networkStatus: navigator.onLine ? 'online' : 'offline'
      })
    } catch (error) {
      console.error('加载数据统计失败:', error)
    }
  }
  
  const startPerformanceMonitoring = () => {
    setIsMonitoring(true)
    updatePerformanceMetrics()
  }
  
  const updatePerformanceMetrics = () => {
    const perfMetrics = performanceMonitor.getMetrics()
    const perfSummary = performanceMonitor.getSummary()
    const memStats = memoryManager.getMemoryStats()
    const renderStats = renderOptimizer.getRenderStats()
    const netStats = networkOptimizer.getNetworkStats()
    
    setPerformanceMetrics({
      performance: perfMetrics,
      summary: perfSummary,
      memory: memStats,
      render: renderStats,
      network: netStats
    })
  }
  
  const handleSync = async () => {
    try {
      const result = await simpleDataPersistenceService.syncData()
      if (result.success) {
        toast.success('数据同步成功')
        loadDataStats()
      } else {
        toast.error('数据同步失败')
      }
    } catch (error) {
      toast.error('数据同步失败')
    }
  }
  
  const handleCreateBackup = async () => {
    try {
      const allData = await simpleDataPersistenceService.getAllData('guest')
      if (allData.success) {
        const backup = {
          version: '1.0.0',
          timestamp: Date.now(),
          data: allData.data
        }
        
        const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `backup_${new Date().toISOString().split('T')[0]}.json`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
        
        toast.success('备份导出成功')
      } else {
        toast.error('备份创建失败')
      }
    } catch (error) {
      toast.error('备份创建失败')
    }
  }
  
  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }
  
  const formatDate = (timestamp) => {
    if (!timestamp) return '从未'
    return new Date(timestamp).toLocaleString('zh-CN')
  }
  
  const formatTime = (ms) => {
    if (!ms) return '0ms'
    if (ms < 1000) return `${ms.toFixed(2)}ms`
    return `${(ms / 1000).toFixed(2)}s`
  }
  
  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-400'
    if (score >= 70) return 'text-yellow-400'
    return 'text-red-400'
  }
  
  // 检查存储空间
  const checkStorageSpace = async () => {
    try {
      // 检查localStorage
      const localUsed = JSON.stringify(localStorage).length
      const localMaxSize = 3 * 1024 * 1024 // 3MB
      const localUsagePercent = (localUsed / localMaxSize) * 100
      const localItemCount = localStorage.length
      
      // 检查本地云服务
      const cloudStats = await localCloudService.getStorageStats()
      
      // 获取所有存储项信息
      const localItems = []
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key) {
          const value = localStorage.getItem(key)
          const size = new Blob([value]).size
          localItems.push({
            key,
            size,
            sizeKB: (size / 1024).toFixed(2),
            lastModified: new Date().toISOString(),
            type: 'localStorage'
          })
        }
      }
      
      // 获取云存储项
      const cloudItems = cloudStats.files.map(file => ({
        key: file.name,
        size: file.size,
        sizeKB: (file.size / 1024).toFixed(2),
        lastModified: file.timestamp,
        type: file.isImage ? 'image' : 'cloud'
      }))
      
      // 合并所有项目
      const allItems = [...localItems, ...cloudItems].sort((a, b) => b.size - a.size)
      
      const totalUsed = localUsed + cloudStats.totalSize
      const totalMaxSize = localMaxSize + (200 * 1024 * 1024) // 3MB + 200MB
      const totalUsagePercent = (totalUsed / totalMaxSize) * 100
      
      setStorageInfo({
        used: totalUsed,
        maxSize: totalMaxSize,
        usagePercent: totalUsagePercent,
        itemCount: localItemCount + cloudStats.fileCount,
        items: allItems,
        localStats: {
          used: localUsed,
          maxSize: localMaxSize,
          usagePercent: localUsagePercent,
          itemCount: localItemCount
        },
        cloudStats: cloudStats
      })
    } catch (error) {
      console.error('检查存储空间时出错:', error)
    }
  }
  
  // 清理存储空间
  const cleanupStorage = async () => {
    setIsCleaning(true)
    try {
      // 清理旧的用户编辑数据
      const keysToRemove = []
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && (key.startsWith('user-edited-') || key.startsWith('ai-story-'))) {
          keysToRemove.push(key)
        }
      }
      
      // 按时间排序，删除最旧的10个
      keysToRemove.sort().slice(0, 10).forEach(key => {
        localStorage.removeItem(key)
      })
      
      // 清理可能损坏的数据
      const altverseKeys = []
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && key.startsWith('altverse-')) {
          try {
            const data = JSON.parse(localStorage.getItem(key))
            if (JSON.stringify(data).includes('NaN') || 
                JSON.stringify(data).includes('undefined') ||
                JSON.stringify(data).includes('[object Object]')) {
              altverseKeys.push(key)
            }
          } catch (error) {
            altverseKeys.push(key)
          }
        }
      }
      
      // 删除损坏的数据
      altverseKeys.forEach(key => {
        localStorage.removeItem(key)
      })
      
      // 清理本地云服务中的旧文件
      await localCloudService.cleanupOldFiles(7) // 清理7天前的文件
      
      toast.success('存储空间清理完成')
      
      // 重新检查存储空间
      setTimeout(() => {
        checkStorageSpace()
        setIsCleaning(false)
      }, 1000)
    } catch (error) {
      console.error('清理存储空间时出错:', error)
      toast.error('清理失败')
      setIsCleaning(false)
    }
  }
  
  // 清空所有存储
  const clearAllStorage = () => {
    if (window.confirm('确定要清空所有存储数据吗？此操作不可恢复！')) {
      localStorage.clear()
      checkStorageSpace()
      toast.success('所有存储已清空')
    }
  }
  
  // 删除特定存储项
  const removeItem = async (key) => {
    if (window.confirm(`确定要删除 "${key}" 吗？`)) {
      try {
        // 检查是localStorage还是云存储
        if (localStorage.getItem(key)) {
          localStorage.removeItem(key)
        } else {
          await localCloudService.deleteFile(key)
        }
        await checkStorageSpace()
        toast.success('删除成功')
      } catch (error) {
        console.error('删除存储项失败:', error)
        toast.error('删除失败')
      }
    }
  }
  
  // 导出存储数据
  const exportStorage = async () => {
    try {
      // 导出所有数据（包括云存储）
      const allData = await localCloudService.exportAllData()
      
      const data = {
        stories,
        characters,
        cloudData: allData,
        timestamp: new Date().toISOString(),
        storageInfo: await checkStorageSpace()
      }
      
      const dataStr = JSON.stringify(data, null, 2)
      const dataBlob = new Blob([dataStr], { type: 'application/json' })
      
      const url = URL.createObjectURL(dataBlob)
      const link = document.createElement('a')
      link.href = url
      link.download = `altverse-backup-${new Date().toISOString().split('T')[0]}.json`
      link.click()
      
      URL.revokeObjectURL(url)
      toast.success('数据导出成功')
    } catch (error) {
      console.error('导出存储数据失败:', error)
      toast.error('导出失败')
    }
  }
  
  const getStorageColor = (percent) => {
    if (percent > 90) return 'text-red-500'
    if (percent > 70) return 'text-yellow-500'
    return 'text-green-500'
  }
  
  const getStorageBgColor = (percent) => {
    if (percent > 90) return 'bg-red-500'
    if (percent > 70) return 'bg-yellow-500'
    return 'bg-green-500'
  }

  const handleSaveSettings = async () => {
    setIsSaving(true)
    try {
      // 保存应用设置
      settingsService.saveSettings(settings)
      
      // 保存用户偏好
      localStorage.setItem('userPreferences', JSON.stringify(preferences))
      
      // 更新用户信息
      if (user) {
        await updateUser({ ...user, preferences })
      }
      
      toast.success('设置已保存')
    } catch (error) {
      toast.error('保存设置失败')
      console.error('保存设置错误:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const handleResetSettings = () => {
    if (window.confirm('确定要重置所有设置吗？此操作不可撤销。')) {
      settingsService.resetSettings()
      setSettings(settingsService.getSettings())
      setPreferences({
        language: '中文',
        theme: 'dark',
        autoSave: true,
        notifications: true,
        soundEffects: true,
        animations: true
      })
      toast.success('设置已重置')
    }
  }

  const renderProfileTab = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold text-white">个人资料</h3>
      
      <div className="bg-dark-700 p-6 rounded-lg">
        <div className="flex items-center space-x-4 mb-6">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
            <User size={32} className="text-white" />
          </div>
          <div>
            <h4 className="text-lg font-medium text-white">{user?.username || '未登录'}</h4>
            <p className="text-dark-300">用户ID: {user?.id || 'guest'}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-dark-300 mb-2">用户名</label>
            <input
              type="text"
              value={user?.username || ''}
              disabled
              className="w-full px-3 py-2 bg-dark-600 text-white rounded-lg border border-dark-500 focus:border-blue-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-dark-300 mb-2">邮箱</label>
            <input
              type="email"
              value={user?.email || ''}
              disabled
              className="w-full px-3 py-2 bg-dark-600 text-white rounded-lg border border-dark-500 focus:border-blue-500 focus:outline-none"
            />
          </div>
        </div>
      </div>
    </div>
  )

  const renderPreferencesTab = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold text-white">偏好设置</h3>
      
      <div className="bg-dark-700 p-6 rounded-lg">
        <h4 className="text-lg font-medium text-white mb-4">界面设置</h4>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-dark-300">语言</label>
              <p className="text-sm text-dark-400">选择界面显示语言</p>
            </div>
            <select
              value={preferences.language}
              onChange={(e) => setPreferences({...preferences, language: e.target.value})}
              className="bg-dark-600 text-white px-3 py-2 rounded-lg border border-dark-500 focus:border-blue-500 focus:outline-none"
            >
              <option value="中文">中文</option>
              <option value="English">English</option>
              <option value="日本語">日本語</option>
            </select>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className="text-dark-300">主题</label>
              <p className="text-sm text-dark-400">选择界面主题</p>
            </div>
            <select
              value={preferences.theme}
              onChange={(e) => setPreferences({...preferences, theme: e.target.value})}
              className="bg-dark-600 text-white px-3 py-2 rounded-lg border border-dark-500 focus:border-blue-500 focus:outline-none"
            >
              <option value="dark">深色主题</option>
              <option value="light">浅色主题</option>
              <option value="auto">跟随系统</option>
            </select>
          </div>
        </div>
      </div>

      <div className="bg-dark-700 p-6 rounded-lg">
        <h4 className="text-lg font-medium text-white mb-4">功能设置</h4>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-dark-300">自动保存</label>
              <p className="text-sm text-dark-400">自动保存编辑内容</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={preferences.autoSave}
                onChange={(e) => setPreferences({...preferences, autoSave: e.target.checked})}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className="text-dark-300">通知</label>
              <p className="text-sm text-dark-400">接收系统通知</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={preferences.notifications}
                onChange={(e) => setPreferences({...preferences, notifications: e.target.checked})}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className="text-dark-300">音效</label>
              <p className="text-sm text-dark-400">播放操作音效</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={preferences.soundEffects}
                onChange={(e) => setPreferences({...preferences, soundEffects: e.target.checked})}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className="text-dark-300">动画效果</label>
              <p className="text-sm text-dark-400">启用界面动画</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={preferences.animations}
                onChange={(e) => setPreferences({...preferences, animations: e.target.checked})}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </div>
      </div>
    </div>
  )

  const renderDataTab = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold text-white">数据管理</h3>
      
      {/* 存储概览 */}
      {dataStats && (
        <div className="bg-dark-700 p-6 rounded-lg">
          <h4 className="text-lg font-medium text-white mb-4">存储概览</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-dark-600 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <HardDrive className="text-blue-400" size={16} />
                <span className="text-sm text-dark-300">总大小</span>
              </div>
              <div className="text-xl font-bold text-white">
                {formatBytes(dataStats.totalSize)}
              </div>
            </div>
            <div className="bg-dark-600 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Database className="text-green-400" size={16} />
                <span className="text-sm text-dark-300">项目数量</span>
              </div>
              <div className="text-xl font-bold text-white">
                {dataStats.itemCount || 0}
              </div>
            </div>
            <div className="bg-dark-600 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="text-yellow-400" size={16} />
                <span className="text-sm text-dark-300">最后同步</span>
              </div>
              <div className="text-sm text-white">
                {formatDate(dataStats.lastSync)}
              </div>
            </div>
            <div className="bg-dark-600 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="text-green-400" size={16} />
                <span className="text-sm text-dark-300">压缩状态</span>
              </div>
              <div className="text-sm text-white">
                {dataStats.compressionEnabled ? '已启用' : '已禁用'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 数据同步 */}
      <div className="bg-dark-700 p-6 rounded-lg">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-medium text-white">数据同步</h4>
          <button
            onClick={handleSync}
            className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/80 text-white rounded-lg transition-colors"
          >
            <RefreshCw size={16} />
            立即同步
          </button>
        </div>
        {syncStatus && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${
                syncStatus.inProgress ? 'bg-yellow-400' : 
                syncStatus.networkStatus === 'online' ? 'bg-green-400' : 'bg-red-400'
              }`} />
              <span className="text-white">
                {syncStatus.inProgress ? '同步中...' : 
                 syncStatus.networkStatus === 'online' ? '在线' : '离线'}
              </span>
            </div>
            <div className="text-sm text-dark-300">
              最后同步: {formatDate(syncStatus.lastSync)}
            </div>
            <div className="text-sm text-dark-300">
              冲突数量: {syncStatus.conflictCount}
            </div>
          </div>
        )}
      </div>

      {/* 数据备份 */}
      <div className="bg-dark-700 p-6 rounded-lg">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-medium text-white">数据备份</h4>
          <button
            onClick={handleCreateBackup}
            className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
          >
            <Download size={16} />
            创建备份
          </button>
        </div>
        <p className="text-sm text-dark-300">
          备份所有数据到本地文件，用于数据恢复和迁移。
        </p>
      </div>

      {/* 存储空间管理 */}
      <div className="bg-dark-700 p-6 rounded-lg">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-medium text-white">存储空间管理</h4>
          <button
            onClick={() => setShowStorageManager(true)}
            className="flex items-center px-3 py-1 text-sm bg-primary/20 text-primary rounded hover:bg-primary/30 transition-colors"
          >
            <HardDrive size={16} className="mr-1" />
            详细管理
          </button>
        </div>
        
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-dark-300">已使用存储</span>
            <span className={`font-mono ${
              parseFloat(storageUsage?.percentage || 0) >= 80 ? 'text-red-400' : 'text-white'
            }`}>
              {storageUsage?.formattedTotalSize || '计算中...'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-dark-300">可用存储</span>
            <span className="text-white font-mono">
              {storageUsage ? imageStorageService.formatSize((5 * 1024 * 1024) - (storageUsage.totalSize || 0)) : '计算中...'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-dark-300">使用率</span>
            <span className={`font-mono font-semibold ${
              parseFloat(storageUsage?.percentage || 0) >= 90 ? 'text-red-500' :
              parseFloat(storageUsage?.percentage || 0) >= 80 ? 'text-orange-500' :
              parseFloat(storageUsage?.percentage || 0) >= 60 ? 'text-yellow-500' : 'text-green-500'
            }`}>
              {storageUsage?.percentage || '0'}%
            </span>
          </div>
          <div className="w-full bg-dark-600 rounded-full h-3 overflow-hidden">
            <div 
              className={`h-3 rounded-full transition-all duration-500 ${
                parseFloat(storageUsage?.percentage || 0) >= 90 ? 'bg-red-500' :
                parseFloat(storageUsage?.percentage || 0) >= 80 ? 'bg-orange-500' :
                parseFloat(storageUsage?.percentage || 0) >= 60 ? 'bg-yellow-500' : 'bg-green-500'
              }`}
              style={{ width: `${Math.min(storageUsage?.percentage || 0, 100)}%` }}
            />
          </div>
          
          {parseFloat(storageUsage?.percentage || 0) >= 60 && (
            <div className="mt-4 p-3 bg-orange-500/10 border border-orange-500/30 rounded">
              <p className="text-orange-400 text-sm">
                ⚠️ 存储空间使用率较高，建议执行智能清理或删除不必要的数据
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
  
  const renderPerformanceTab = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold text-white">性能监控</h3>
      
      {/* 性能概览 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-dark-700 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-dark-300 text-sm">整体性能</p>
              <p className={`text-2xl font-bold ${getScoreColor(performanceMetrics.summary?.overallScore || 0)}`}>
                {performanceMetrics.summary?.overallScore || 0}
              </p>
            </div>
            {performanceMetrics.summary?.overallScore >= 90 ? 
              <CheckCircle className="w-6 h-6 text-green-400" /> :
              performanceMetrics.summary?.overallScore >= 70 ?
              <AlertTriangle className="w-6 h-6 text-yellow-400" /> :
              <AlertTriangle className="w-6 h-6 text-red-400" />}
          </div>
        </div>

        <div className="bg-dark-700 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-dark-300 text-sm">页面加载</p>
              <p className={`text-2xl font-bold ${getScoreColor(performanceMetrics.summary?.pageLoadScore || 0)}`}>
                {performanceMetrics.summary?.pageLoadScore || 0}
              </p>
            </div>
            <Activity className="w-6 h-6 text-blue-400" />
          </div>
        </div>

        <div className="bg-dark-700 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-dark-300 text-sm">资源加载</p>
              <p className={`text-2xl font-bold ${getScoreColor(performanceMetrics.summary?.resourceScore || 0)}`}>
                {performanceMetrics.summary?.resourceScore || 0}
              </p>
            </div>
            <HardDrive className="w-6 h-6 text-purple-400" />
          </div>
        </div>

        <div className="bg-dark-700 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-dark-300 text-sm">运行时</p>
              <p className={`text-2xl font-bold ${getScoreColor(performanceMetrics.summary?.runtimeScore || 0)}`}>
                {performanceMetrics.summary?.runtimeScore || 0}
              </p>
            </div>
            <Cpu className="w-6 h-6 text-green-400" />
          </div>
        </div>
      </div>

      {/* 内存管理 */}
      {performanceMetrics.memory && (
        <div className="bg-dark-700 p-6 rounded-lg">
          <h4 className="text-lg font-medium text-white mb-4">内存管理</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-dark-300 text-sm mb-1">已使用内存</p>
              <p className="text-white font-mono">{formatBytes(performanceMetrics.memory.used || 0)}</p>
            </div>
            <div>
              <p className="text-dark-300 text-sm mb-1">总内存</p>
              <p className="text-white font-mono">{formatBytes(performanceMetrics.memory.total || 0)}</p>
            </div>
            <div>
              <p className="text-dark-300 text-sm mb-1">使用率</p>
              <p className="text-white font-mono">
                {performanceMetrics.memory.total > 0 
                  ? `${((performanceMetrics.memory.used / performanceMetrics.memory.total) * 100).toFixed(1)}%`
                  : '0%'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* 网络性能 */}
      {performanceMetrics.network && (
        <div className="bg-dark-700 p-6 rounded-lg">
          <h4 className="text-lg font-medium text-white mb-4">网络性能</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-dark-300 text-sm mb-1">连接状态</p>
              <p className="text-white">
                {performanceMetrics.network.online ? 
                  <span className="text-green-400">在线</span> : 
                  <span className="text-red-400">离线</span>}
              </p>
            </div>
            <div>
              <p className="text-dark-300 text-sm mb-1">平均延迟</p>
              <p className="text-white font-mono">
                {formatTime(performanceMetrics.network.averageLatency || 0)}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* 自动刷新设置 */}
      <div className="bg-dark-700 p-6 rounded-lg">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-lg font-medium text-white">自动刷新</h4>
            <p className="text-sm text-dark-300">每5秒自动更新性能指标</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-dark-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
          </label>
        </div>
      </div>
    </div>
  )
  
  const renderStorageTab = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold text-white">存储空间管理</h3>
      
      {/* 存储空间概览 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-dark-700 p-4 rounded-lg">
          <div className="text-sm text-dark-300 mb-1">已使用空间</div>
          <div className="text-2xl font-bold text-blue-400">
            {(storageInfo.used / 1024 / 1024).toFixed(2)} MB
          </div>
        </div>
        
        <div className="bg-dark-700 p-4 rounded-lg">
          <div className="text-sm text-dark-300 mb-1">总空间</div>
          <div className="text-2xl font-bold text-green-400">
            {(storageInfo.maxSize / 1024 / 1024).toFixed(2)} MB
          </div>
        </div>
        
        <div className="bg-dark-700 p-4 rounded-lg">
          <div className="text-sm text-dark-300 mb-1">使用率</div>
          <div className={`text-2xl font-bold ${getStorageColor(storageInfo.usagePercent)}`}>
            {storageInfo.usagePercent.toFixed(1)}%
          </div>
        </div>
        
        <div className="bg-dark-700 p-4 rounded-lg">
          <div className="text-sm text-dark-300 mb-1">存储项数</div>
          <div className="text-2xl font-bold text-purple-400">
            {storageInfo.itemCount}
          </div>
        </div>
      </div>

      {/* 存储类型详情 */}
      {storageInfo.localStats && storageInfo.cloudStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-dark-700 p-6 rounded-lg">
            <h4 className="text-lg font-medium text-white mb-4">📱 本地存储 (localStorage)</h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-dark-300">已使用:</span>
                <span className="text-white font-semibold">{(storageInfo.localStats.used / 1024).toFixed(2)} KB</span>
              </div>
              <div className="flex justify-between">
                <span className="text-dark-300">使用率:</span>
                <span className="text-white font-semibold">{storageInfo.localStats.usagePercent.toFixed(1)}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-dark-300">项目数:</span>
                <span className="text-white font-semibold">{storageInfo.localStats.itemCount}</span>
              </div>
            </div>
          </div>
          
          <div className="bg-dark-700 p-6 rounded-lg">
            <h4 className="text-lg font-medium text-white mb-4">☁️ 本地云存储 (IndexedDB)</h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-dark-300">已使用:</span>
                <span className="text-white font-semibold">{(storageInfo.cloudStats.totalSize / 1024 / 1024).toFixed(2)} MB</span>
              </div>
              <div className="flex justify-between">
                <span className="text-dark-300">总容量:</span>
                <span className="text-white font-semibold">200 MB</span>
              </div>
              <div className="flex justify-between">
                <span className="text-dark-300">文件数:</span>
                <span className="text-white font-semibold">{storageInfo.cloudStats.fileCount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-dark-300">平均文件大小:</span>
                <span className="text-white font-semibold">
                  {storageInfo.cloudStats.fileCount > 0 
                    ? (storageInfo.cloudStats.totalSize / storageInfo.cloudStats.fileCount / 1024).toFixed(2) + ' KB'
                    : '0 KB'
                  }
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 存储空间进度条 */}
      <div className="bg-dark-700 p-6 rounded-lg">
        <div className="flex justify-between text-sm text-dark-300 mb-2">
          <span>存储空间使用情况</span>
          <span>{storageInfo.usagePercent.toFixed(1)}%</span>
        </div>
        <div className="w-full bg-dark-600 rounded-full h-4">
          <div 
            className={`h-4 rounded-full ${getStorageBgColor(storageInfo.usagePercent)}`}
            style={{ width: `${Math.min(storageInfo.usagePercent, 100)}%` }}
          ></div>
        </div>
      </div>

      {/* 操作按钮 */}
      <div className="bg-dark-700 p-6 rounded-lg">
        <h4 className="text-lg font-medium text-white mb-4">操作</h4>
        <div className="flex flex-wrap gap-4">
          <button
            onClick={cleanupStorage}
            disabled={isCleaning}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
          >
            <Trash2 size={16} />
            {isCleaning ? '清理中...' : '清理存储空间'}
          </button>
          
          <button
            onClick={checkStorageSpace}
            className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
          >
            <RefreshCw size={16} />
            刷新状态
          </button>
          
          <button
            onClick={exportStorage}
            className="flex items-center gap-2 px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition-colors"
          >
            <Download size={16} />
            导出数据
          </button>
          
          <button
            onClick={clearAllStorage}
            className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
          >
            <Trash2 size={16} />
            清空所有存储
          </button>
        </div>
      </div>

      {/* 存储项详情 */}
      <div className="bg-dark-700 p-6 rounded-lg">
        <h4 className="text-lg font-medium text-white mb-4">存储项详情</h4>
        
        {storageInfo.items.length === 0 ? (
          <div className="text-dark-400 text-center py-8">暂无存储数据</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-dark-600">
                  <th className="text-left py-2 px-3 text-dark-300">键名</th>
                  <th className="text-center py-2 px-3 text-dark-300">类型</th>
                  <th className="text-right py-2 px-3 text-dark-300">大小</th>
                  <th className="text-right py-2 px-3 text-dark-300">操作</th>
                </tr>
              </thead>
              <tbody>
                {storageInfo.items.slice(0, 20).map((item, index) => (
                  <tr key={index} className="border-b border-dark-600 hover:bg-dark-600">
                    <td className="py-2 px-3 font-mono text-xs break-all text-white">
                      {item.key}
                    </td>
                    <td className="py-2 px-3 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        item.type === 'localStorage' 
                          ? 'bg-dark-600 text-dark-300' 
                          : item.type === 'image'
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-blue-500/20 text-blue-400'
                      }`}>
                        {item.type === 'localStorage' ? '📱 本地' : 
                         item.type === 'image' ? '🖼️ 图片' : '☁️ 云存储'}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-right text-white">
                      {item.sizeKB} KB
                    </td>
                    <td className="py-2 px-3 text-right">
                      <button
                        onClick={() => removeItem(item.key)}
                        className="text-red-400 hover:text-red-300 text-xs transition-colors"
                      >
                        删除
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {storageInfo.items.length > 20 && (
              <div className="text-center text-dark-400 text-sm mt-4">
                显示前20项，共 {storageInfo.items.length} 项
              </div>
            )}
          </div>
        )}
      </div>

      {/* 优化建议 */}
      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
        <h4 className="text-lg font-semibold text-yellow-400 mb-2">💡 优化建议</h4>
        <ul className="text-sm text-yellow-300 space-y-1">
          <li>• 本地云存储支持200MB大容量，可放心创作复杂内容</li>
          <li>• 小文件自动使用localStorage，大文件自动使用IndexedDB</li>
          <li>• 系统会自动选择最佳存储方式，无需手动干预</li>
          <li>• 定期导出重要数据作为备份</li>
          <li>• 超过7天的旧文件会自动清理，保持存储空间充足</li>
        </ul>
      </div>
    </div>
  )

  const renderHelpTab = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold text-white">帮助与支持</h3>
      
      <div className="bg-dark-700 p-6 rounded-lg">
        <h4 className="text-lg font-medium text-white mb-4">使用指南</h4>
        
        <div className="space-y-4">
          <div className="flex items-start">
            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center mr-3 mt-1">
              <span className="text-white font-bold text-sm">1</span>
            </div>
            <div>
              <h5 className="text-white font-medium">创建故事</h5>
              <p className="text-dark-300 text-sm">点击"开始创作"按钮，选择故事类型和设定，AI将为您生成精彩故事。</p>
            </div>
          </div>
          
          <div className="flex items-start">
            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center mr-3 mt-1">
              <span className="text-white font-bold text-sm">2</span>
            </div>
            <div>
              <h5 className="text-white font-medium">编辑故事</h5>
              <p className="text-dark-300 text-sm">在故事编辑器中修改情节、角色和对话，打造属于您的独特故事。</p>
            </div>
          </div>
          
          <div className="flex items-start">
            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center mr-3 mt-1">
              <span className="text-white font-bold text-sm">3</span>
            </div>
            <div>
              <h5 className="text-white font-medium">播放故事</h5>
              <p className="text-dark-300 text-sm">在故事播放器中体验您的创作，享受沉浸式的阅读体验。</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-dark-700 p-6 rounded-lg">
        <h4 className="text-lg font-medium text-white mb-4">常见问题</h4>
        
        <div className="space-y-3">
          <details className="group">
            <summary className="flex justify-between items-center cursor-pointer text-white hover:text-blue-400 transition-colors">
              <span>如何保存我的故事？</span>
              <span className="group-open:rotate-180 transition-transform">▼</span>
            </summary>
            <div className="mt-2 text-dark-300 text-sm">
              您的故事会自动保存到本地存储。您也可以手动导出故事文件进行备份。
            </div>
          </details>
          
          <details className="group">
            <summary className="flex justify-between items-center cursor-pointer text-white hover:text-blue-400 transition-colors">
              <span>如何分享我的故事？</span>
              <span className="group-open:rotate-180 transition-transform">▼</span>
            </summary>
            <div className="mt-2 text-dark-300 text-sm">
              您可以将故事导出为文本文件，然后通过社交媒体或其他方式分享给朋友。
            </div>
          </details>
          
          <details className="group">
            <summary className="flex justify-between items-center cursor-pointer text-white hover:text-blue-400 transition-colors">
              <span>故事生成失败怎么办？</span>
              <span className="group-open:rotate-180 transition-transform">▼</span>
            </summary>
            <div className="mt-2 text-dark-300 text-sm">
              请检查网络连接，或尝试重新生成。如果问题持续存在，请联系技术支持。
            </div>
          </details>
        </div>
      </div>
    </div>
  )

  return (
    <>
      {/* 存储监控弹窗 */}
      {showStorageMonitor && (
        <StorageMonitor onClose={() => setShowStorageMonitor(false)} />
      )}
      
      {/* 主设置界面 */}
      <div className={`fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
        <div className="bg-dark-800 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col transform transition-transform duration-300 scale-95">
        <div className="flex justify-between items-center p-5 border-b border-dark-700">
          <h2 className="text-2xl font-bold text-white">设置</h2>
          <button onClick={onClose} className="text-dark-300 hover:text-white transition-colors">
            <X size={24} />
          </button>
        </div>

        <div className="flex flex-grow overflow-hidden">
          {/* 侧边导航 */}
          <nav className="w-48 bg-dark-900 p-4 space-y-2 border-r border-dark-700">
            <button
              onClick={() => setActiveTab('profile')}
              className={`w-full text-left px-4 py-2 rounded-lg flex items-center transition-colors ${activeTab === 'profile' ? 'bg-dark-700 text-white' : 'text-dark-300 hover:bg-dark-700 hover:text-white'}`}
            >
              <User size={18} className="mr-2" /> 个人资料
            </button>
            <button
              onClick={() => setActiveTab('preferences')}
              className={`w-full text-left px-4 py-2 rounded-lg flex items-center transition-colors ${activeTab === 'preferences' ? 'bg-dark-700 text-white' : 'text-dark-300 hover:bg-dark-700 hover:text-white'}`}
            >
              <Palette size={18} className="mr-2" /> 偏好设置
            </button>
            <button
              onClick={() => setActiveTab('data')}
              className={`w-full text-left px-4 py-2 rounded-lg flex items-center transition-colors ${activeTab === 'data' ? 'bg-dark-700 text-white' : 'text-dark-300 hover:bg-dark-700 hover:text-white'}`}
            >
              <Database size={18} className="mr-2" /> 数据管理
            </button>
            <button
              onClick={() => setActiveTab('performance')}
              className={`w-full text-left px-4 py-2 rounded-lg flex items-center transition-colors ${activeTab === 'performance' ? 'bg-dark-700 text-white' : 'text-dark-300 hover:bg-dark-700 hover:text-white'}`}
            >
              <Activity size={18} className="mr-2" /> 性能监控
            </button>
            <button
              onClick={() => setActiveTab('storage')}
              className={`w-full text-left px-4 py-2 rounded-lg flex items-center transition-colors ${activeTab === 'storage' ? 'bg-dark-700 text-white' : 'text-dark-300 hover:bg-dark-700 hover:text-white'}`}
            >
              <HardDrive size={18} className="mr-2" /> 存储空间
            </button>
            <button
              onClick={() => setActiveTab('help')}
              className={`w-full text-left px-4 py-2 rounded-lg flex items-center transition-colors ${activeTab === 'help' ? 'bg-dark-700 text-white' : 'text-dark-300 hover:bg-dark-700 hover:text-white'}`}
            >
              <HelpCircle size={18} className="mr-2" /> 帮助支持
            </button>
          </nav>

          {/* 内容区域 */}
          <div className="flex-grow p-6 overflow-y-auto">
            {activeTab === 'profile' && renderProfileTab()}
            {activeTab === 'preferences' && renderPreferencesTab()}
            {activeTab === 'data' && renderDataTab()}
            {activeTab === 'performance' && renderPerformanceTab()}
            {activeTab === 'storage' && renderStorageTab()}
            {activeTab === 'help' && renderHelpTab()}
          </div>
        </div>

        {/* 底部操作按钮 */}
        <div className="flex justify-between items-center p-5 border-t border-dark-700">
          <button
            onClick={handleResetSettings}
            className="flex items-center px-4 py-2 text-dark-300 hover:text-white transition-colors"
          >
            <RotateCcw size={18} className="mr-2" />
            重置设置
          </button>
          
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-dark-600 text-white rounded-lg hover:bg-dark-500 transition-colors"
            >
              取消
            </button>
            <button
              onClick={handleSaveSettings}
              disabled={isSaving}
              className="flex items-center px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  保存中...
                </>
              ) : (
                <>
                  <Save size={18} className="mr-2" />
                  保存设置
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      </div>
    </>
  )
}

export default UserSettings

